import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiServices } from '../../services/apiServices';

// Async thunk for login
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await apiServices.postAPI('login', { email, password });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Login failed' });
    }
  }
);

// Async thunk for sending OTP login otp
export const sendLoginToken = createAsyncThunk(
  'auth/sendLoginToken',
  async (email, { rejectWithValue }) => {
    try {
      const response = await apiServices.postAPI('send-login-token', { email });
      return response.data;
    } catch (error) {
      return rejectWithValue({message: error.response?.data?.error} || { message: 'Failed to send OTP' });
    }
  }
);

// Async thunk for validating OTP login otp
export const validateLoginToken = createAsyncThunk(
  'auth/validateLoginToken',
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await apiServices.postAPI('validate-login-token', { email, otp });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to validate OTP' });
    }
  }
);

// Async thunk for checking email
export const checkUserEmail = createAsyncThunk(
  'auth/checkUserEmail',
  async (email, { rejectWithValue }) => {
    try {
      const response = await apiServices.getAPI(`check-email/${encodeURIComponent(email)}`);
      
      // Check payment status and add it to the response
      const responseData = response.data;
      if (responseData.user && responseData.user.payment_status) {
        responseData.paymentStatus = responseData.user.payment_status;
        responseData.requiresPayment = responseData.user.payment_status !== 'COMPLETED';
      
    }
      
      return responseData;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Email check failed' });
    }
  }
);

// Async thunk for fetching admin users - includes cookies via withCredentials
export const fetchAdminUsers = createAsyncThunk(
  'auth/fetchAdminUsers',
  async ({ page = 1, limit = 10, search = '' }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (search.trim()) {
        params.append('search', search.trim());
      }

      // apiServices.getAPI already includes withCredentials: true, which sends cookies automatically
      const response = await apiServices.getAPI(`admin/users?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch admin users' });
    }
  }
);

const initialState = {
  user: null,
  otp: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  currentUser: null, // For storing user data during email check
  showPasswordField: false,
  // OTP related fields
  otpEmail: null,
  otpSent: false,
  otpLoading: false,
  otpError: null,
  resendTimer: 0,
  // Payment related fields
  paymentStatus: null,
  requiresPayment: false,
  showPaymentModal: false,
  // Admin users related fields
  adminUsers: [],
  adminUsersLoading: false,
  adminUsersError: null,
  adminUsersPagination: {
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 1
  },
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.error = null;
    },
    setToken: (state, action) => {
      state.otp = action.payload;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
    },
    setShowPasswordField: (state, action) => {
      state.showPasswordField = action.payload;
    },
    clearUser: (state) => {
      state.user = null;
      state.otp = null;
      state.isAuthenticated = false;
      state.error = null;
      state.currentUser = null;
      state.showPasswordField = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    // OTP related reducers
    setOtpEmail: (state, action) => {
      state.otpEmail = action.payload;
    },
    setOtpSent: (state, action) => {
      state.otpSent = action.payload;
    },
    setOtpLoading: (state, action) => {
      state.otpLoading = action.payload;
    },
    setOtpError: (state, action) => {
      state.otpError = action.payload;
    },
    setResendTimer: (state, action) => {
      state.resendTimer = action.payload;
    },
    // Payment related reducers
    setPaymentStatus: (state, action) => {
      state.paymentStatus = action.payload;
    },
    setRequiresPayment: (state, action) => {
      state.requiresPayment = action.payload;
    },
    setShowPaymentModal: (state, action) => {
      state.showPaymentModal = action.payload;
    },
    clearOtpState: (state, action) => {
      state.otpEmail = null;
      state.otpSent = false;
      state.otpLoading = false;
      state.otpError = null;
      state.resendTimer = 0;
      // Clear payment state as well
      state.paymentStatus = null;
      state.requiresPayment = false;
      state.showPaymentModal = false;
    },
    logout: (state) => {
      // Clear Redux state
      state.user = null;
      state.otp = null;
      state.isAuthenticated = false;
      state.error = null;
      state.currentUser = null;
      state.showPasswordField = false;
      // Clear OTP state
      state.otpEmail = null;
      state.otpSent = false;
      state.otpLoading = false;
      state.otpError = null;
      state.resendTimer = 0;
      // Clear payment state
      state.paymentStatus = null;
      state.requiresPayment = false;
      state.showPaymentModal = false;
      
      // Note: persistor.purge() will be called from the component to avoid circular dependency
      
      // Clear all session-related items from localStorage
      localStorage.removeItem('user');
      localStorage.removeItem('otp');
      localStorage.removeItem('basicAuth');
      localStorage.removeItem('BasicAuth');
      localStorage.removeItem('registrationFormData');
      
      // Clear any other potential authentication-related items
      localStorage.removeItem('auth');
      localStorage.removeItem('Auth');
      localStorage.removeItem('AUTH');
      localStorage.removeItem('authentication');
      localStorage.removeItem('session');
      localStorage.removeItem('Session');
      localStorage.removeItem('SESSION');
      
      // Clear sessionStorage completely
      sessionStorage.clear();
      
      // Clear any cookies that might exist
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
      });
      
      // Clear any potential indexedDB or other browser storage
      if ('indexedDB' in window) {
        // Clear any potential indexedDB data
        indexedDB.databases().then(databases => {
          databases.forEach(db => {
            if (db.name) {
              const deleteRequest = indexedDB.deleteDatabase(db.name);
              deleteRequest.onsuccess = () => console.log('IndexedDB cleared:', db.name);
            }
          });
        });
      }
      
      // Force clear any cached authentication headers
      if (typeof window !== 'undefined') {
        // Clear any potential service worker registrations
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistrations().then(registrations => {
            registrations.forEach(registration => {
              registration.unregister();
            });
          });
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user || action.payload;
        state.otp = action.payload.otp;
        state.error = null;
        // Redux Persist will automatically save to localStorage
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        
        // Check for inactive account
        if (action.payload?.code === 'ACCOUNT_INACTIVE') {
          state.error = 'Your account has been deactivated. Please contact support for assistance.';
        } else {
          state.error = action.payload?.message || 'Login failed';
        }
      })
      // Check email cases
      .addCase(checkUserEmail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkUserEmail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        
        // Set payment status
        if (action.payload.paymentStatus) {
          state.paymentStatus = action.payload.paymentStatus;
          state.requiresPayment = action.payload.requiresPayment;
          state.showPaymentModal = action.payload.requiresPayment;
        }
        
        // Set current user data
        if (action.payload.user) {
          state.currentUser = action.payload.user;
        }
      })
      .addCase(checkUserEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Email check failed';
      })
      // Send OTP cases
      .addCase(sendLoginToken.pending, (state) => {
        state.otpLoading = true;
        state.otpError = null;
      })
      .addCase(sendLoginToken.fulfilled, (state, action) => {
        state.otpLoading = false;
        state.otpSent = true;
        state.otpError = null;
      })
      .addCase(sendLoginToken.rejected, (state, action) => {
        state.otpLoading = false;
        state.otpError = action.payload?.message || 'Failed to send OTP';
      })
      // Validate OTP cases
      .addCase(validateLoginToken.pending, (state) => {
        state.otpLoading = true;
        state.otpError = null;
      })
      .addCase(validateLoginToken.fulfilled, (state, action) => {
        state.otpLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user || action.payload;
        state.otp = action.payload.otp;
        state.otpError = null;
        // Clear OTP state after successful validation
        state.otpEmail = null;
        state.otpSent = false;
        state.resendTimer = 0;
      })
      .addCase(validateLoginToken.rejected, (state, action) => {
        state.otpLoading = false;
        
        // Check for inactive account
        if (action.payload?.code === 'ACCOUNT_INACTIVE') {
          state.otpError = 'Your account has been deactivated. Please contact support for assistance.';
        } else {
          state.otpError = action.payload?.message || 'Failed to validate OTP';
        }
      })
      // Fetch admin users cases - cookies are sent via withCredentials: true in getAPI
      .addCase(fetchAdminUsers.pending, (state) => {
        state.adminUsersLoading = true;
        state.adminUsersError = null;
      })
      .addCase(fetchAdminUsers.fulfilled, (state, action) => {
        state.adminUsersLoading = false;
        state.adminUsersError = null;
        state.adminUsers = action.payload.admins || [];
        state.adminUsersPagination = action.payload.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          total_pages: 1
        };
      })
      .addCase(fetchAdminUsers.rejected, (state, action) => {
        state.adminUsersLoading = false;
        state.adminUsersError = action.payload?.message || 'Failed to fetch admin users';
      });
  },
});

export const {
  setUser,
  setToken,
  setCurrentUser,
  setShowPasswordField,
  clearUser,
  clearError,
  logout,
  setOtpEmail,
  setOtpSent,
  setOtpLoading,
  setOtpError,
  setResendTimer,
  clearOtpState,
  setPaymentStatus,
  setRequiresPayment,
  setShowPaymentModal,
} = authSlice.actions;

export default authSlice.reducer;
